import React, { useEffect, useState, useCallback } from 'react';
import './App.css';
import { useProjects } from './hooks/useProjects';
import { useFileUpload } from './hooks/useFileUpload';
import { useProjectStyles } from './hooks/useProjectStyles';
import { useTranslation } from './contexts/TranslationContext';
import ProjectSelector from './components/ProjectSelector';
import UserInfoForm from './components/UserInfoForm';
import Disclaimer from './components/Disclaimer';
import FileInput from './components/FileInput';
import UploadList from './components/UploadList';
import CompletionModal from './components/CompletionModal';
import LanguageModal from './components/LanguageModal';
import LanguageDropdown from './components/LanguageDropdown';
import Footer from './components/Footer';
import { validateName, validateEmail } from './utils/formValidation';

function App() {
  const { t, language } = useTranslation();
  const [logoSrc, setLogoSrc] = useState('/img/IN_logo.png');

  const resolveLogoSrc = () => {
    const logoVar = getComputedStyle(document.documentElement)
      .getPropertyValue('--project-logo')
      .trim();

    if (!logoVar) return '/img/IN_logo.png';

    if (logoVar.startsWith('url(')) {
      return logoVar.slice(4, -1).replace(/['"]/g, '');
    }

    return logoVar.replace(/['"]/g, '');
  };
  
  // Load user information from localStorage
  const loadCachedUserInfo = () => {
    const cachedName = localStorage.getItem('user_name');
    const cachedEmail = localStorage.getItem('user_email');
    return {
      name: cachedName || '',
      email: cachedEmail || ''
    };
  };

  // Save user information to localStorage
  const saveUserInfo = (name, email) => {
    if (name) localStorage.setItem('user_name', name);
    if (email) localStorage.setItem('user_email', email);
  };

  // User information state
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [videoLanguage, setVideoLanguage] = useState('');
  const [videoCountry, setVideoCountry] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);

  // Completion modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [failedFileNames, setFailedFileNames] = useState([]);

  // Load cached user info on mount
  useEffect(() => {
    const cached = loadCachedUserInfo();
    setUserName(cached.name);
    setUserEmail(cached.email);
  }, []);

  // Save user info to cache when it changes
  useEffect(() => {
    if (userName || userEmail) {
      saveUserInfo(userName, userEmail);
    }
  }, [userName, userEmail]);

  // Use custom hooks for projects and file uploads
  const {
    projects,
    filteredProjects,
    selectedProject,
    setSelectedProject,
    preSelectedProject,
    projectsLoading,
    projectsError,
    loadProjects,
    getProjectName,
  } = useProjects();

  // Manage project-specific styles
  useProjectStyles(selectedProject, projects);

  // Handler called when all uploads for the project have settled
  const handleAllUploadsSettled = useCallback(({ completed, failed }) => {
    // Only show modal if at least one file was completed or failed (ignore cancel-only batches)
    if (completed.length === 0 && failed.length === 0) return;
    const names = failed.map(u => u.originalFileName || u.fileName);
    setFailedFileNames(names);
    setShowCompletionModal(true);
  }, []);

  // Update logo when project changes (after style sheet swaps)
  useEffect(() => {
    // Update immediately using currently available styles
    setLogoSrc(resolveLogoSrc());

    // Stylesheet loads asynchronously; re-check shortly after swap
    const timeout = setTimeout(() => {
      setLogoSrc(resolveLogoSrc());
    }, 50);

    return () => clearTimeout(timeout);
  }, [selectedProject]);

  const {
    uploads,
    loading,
    fileInputRef,
    handleFileSelect,
    handleSubmit,
    startUpload,
    resumeUpload,
    pauseUpload,
    cancelUpload,
    deleteUpload,
    dismissError,
    clearAllUploads,
  } = useFileUpload(selectedProject, {
    userName,
    userEmail,
    videoLanguage,
    videoCountry,
    authorizationConsent: consentGiven
  }, {
    onAllUploadsSettled: handleAllUploadsSettled
  });

  // Set card style on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-style', 'card');
  }, []);

  // Update page title based on selected project
  useEffect(() => {
    const project = projects.find(p => p.id === selectedProject);
    const projectName = project ? project.name : '';
    document.title = projectName 
      ? `inCruises Media Sharing Form | ${projectName}`
      : 'inCruises Media Sharing Form';
  }, [selectedProject, projects]);

  // Check if there are unuploaded files for the current project
  const hasUnuploadedFiles = () => {
    if (!selectedProject) return false;
    
    // Check if there are any uploads for the current project that are not completed or cancelled
    const projectUploads = uploads.filter(u => u.projectId === selectedProject);
    return projectUploads.some(u => 
      u.status !== 'completed' && u.status !== 'cancelled'
    );
  };

  // Validate form before allowing submit
  const isFormValid = () => {
    const nameValidation = validateName(userName);
    const emailValidation = validateEmail(userEmail);
    return nameValidation.valid && emailValidation.valid && consentGiven && selectedProject;
  };

  // Check if all required fields are filled (for file input enabling)
  const areRequiredFieldsValid = () => {
    const nameValidation = validateName(userName);
    const emailValidation = validateEmail(userEmail);
    return nameValidation.valid && emailValidation.valid && consentGiven && selectedProject;
  };

  // Handle submit with validation
  const handleFormSubmit = async () => {
    // Validate form fields
    if (!selectedProject) {
      alert(t('projectRequired'));
      return;
    }

    const nameValidation = validateName(userName);
    const emailValidation = validateEmail(userEmail);

    if (!nameValidation.valid) {
      alert(`${t('fillName')}: ${nameValidation.error}`);
      return;
    }

    if (!emailValidation.valid) {
      alert(`${t('fillEmail')}: ${emailValidation.error}`);
      return;
    }

    if (!consentGiven) {
      alert(t('consentRequired'));
      return;
    }

    // Proceed with upload
    await handleSubmit();
  };

  const handleUploadMore = () => {
    window.location.reload();
  };

  return (
    <div className="App">
      <LanguageModal />
      <CompletionModal
        show={showCompletionModal}
        failedFiles={failedFileNames}
        onUploadMore={handleUploadMore}
      />
      <div className="container">
        <div className="language-top">
          <LanguageDropdown />
        </div>
        <div className="header-with-language">
          <div className="event-logo">
            <img
              src={logoSrc}
              alt={selectedProject && projects.find(p => p.id === selectedProject)
                ? projects.find(p => p.id === selectedProject).name
                : 'inCruises'}
              className="event-image"
            />
          </div>
          <div className="header-content">
            <h1>inCruises Media Sharing Form</h1>
          </div>
        </div>

        {(() => {
          const project = projects.find(p => p.id === selectedProject);
          const guideLink = project?.guideLinks?.[language];
          if (!guideLink) return null;
          return (
            <div className="video-tip-message">
              {t('videoTipMessage')}{' '}
              <a href={guideLink} target="_blank" rel="noopener noreferrer">
                {t('videoTipLinkText')}
              </a>
            </div>
          );
        })()}
        
        <div className="form-section">
          <div className="form-group">
            <ProjectSelector
              preSelectedProject={preSelectedProject}
              projects={projects}
              filteredProjects={filteredProjects}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              projectsLoading={projectsLoading}
              projectsError={projectsError}
              loadProjects={loadProjects}
              loading={loading}
              hasUnuploadedFiles={hasUnuploadedFiles}
            />
          </div>

          <UserInfoForm
            userName={userName}
            setUserName={setUserName}
            userEmail={userEmail}
            setUserEmail={setUserEmail}
            videoLanguage={videoLanguage}
            setVideoLanguage={setVideoLanguage}
            videoCountry={videoCountry}
            setVideoCountry={setVideoCountry}
          />

          <Disclaimer
            consentGiven={consentGiven}
            setConsentGiven={setConsentGiven}
          />

          <FileInput
            fileInputRef={fileInputRef}
            handleFileSelect={handleFileSelect}
            loading={loading}
            selectedProject={selectedProject}
            areRequiredFieldsValid={areRequiredFieldsValid()}
            userName={userName}
            userEmail={userEmail}
            consentGiven={consentGiven}
            preSelectedProject={preSelectedProject}
          />

          <button
            onClick={handleFormSubmit}
            disabled={
              loading || 
              !isFormValid() ||
              uploads.filter(u => u.projectId === selectedProject).length === 0 || 
              uploads.filter(u => u.projectId === selectedProject).some(u => u.status === 'initializing') || 
              uploads.filter(u => u.projectId === selectedProject && u.status === 'ready').length === 0
            }
            className="submit-button"
          >
            {loading ? t('uploading') : t('uploadAllFiles')}
          </button>
        </div>

        <UploadList
          uploads={uploads}
          selectedProject={selectedProject}
          getProjectName={getProjectName}
          startUpload={startUpload}
          resumeUpload={resumeUpload}
          pauseUpload={pauseUpload}
          cancelUpload={cancelUpload}
          deleteUpload={deleteUpload}
          dismissError={dismissError}
          clearAllUploads={clearAllUploads}
        />
        <Footer />
      </div>
    </div>
  );
}

export default App;
